/**
 * Where the welcome tour and the getting-started checklist remember what a
 * person has already seen: this browser's localStorage, one key per user.
 * Nothing goes to the server, so a new device may offer the tour once more
 * (only while the workspace is new, see `isNewWorkspace`).
 */

export interface OnboardingState {
  /** The tour was finished or skipped. */
  tourSeen?: boolean;
  /** Ids of checklist items already ticked. */
  quests?: string[];
  /** The checklist was closed for good. */
  questsDismissed?: boolean;
}

const key = (userId: number | string) => `xale:onboarding:v1:${userId}`;

export function readOnboardingState(userId: number | string | undefined): OnboardingState {
  if (userId == null) return {};
  try {
    const raw = window.localStorage.getItem(key(userId));
    return raw ? (JSON.parse(raw) as OnboardingState) : {};
  } catch {
    return {};
  }
}

export function writeOnboardingState(
  userId: number | string | undefined,
  state: OnboardingState
): void {
  if (userId == null) return;
  try {
    window.localStorage.setItem(key(userId), JSON.stringify(state));
  } catch {
    // Private mode / storage full: the tour simply may show again.
  }
}

/** The tour and checklist start on their own only in a workspace this young. */
const NEW_WORKSPACE_DAYS = 30;

export function isNewWorkspace(createdAt: string | Date | null | undefined): boolean {
  if (!createdAt) return false;
  const created = new Date(createdAt).getTime();
  if (!Number.isFinite(created)) return false;
  return Date.now() - created < NEW_WORKSPACE_DAYS * 24 * 60 * 60 * 1000;
}
